import React, { useMemo, useState } from 'react'
import { AlertMsg, MwButton, MwSelector, MwSpinnerButton } from '../components/ui'
import { useCurrent, useFetch } from '../hooks'
import { getCatalogLabel, getLocalizedStatusOptions, getStatusAppearance, getStatusId, getStatusLabel, normalizeSupportSettings } from '../constants/ticket'
import { MdPrint, MdFileDownload } from 'react-icons/md'

const Reports = () => {
  const { currentTenantId, currentCompanyId, currentBranchId, user, currentLangId } = useCurrent()
  const isAr = currentLangId === 'ar';

  // --- منطق التواريخ الافتراضية (أول وآخر يوم في الشهر الحالي) ---
  const formatDateInput = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  const getDefaultDates = () => {
    const today = new Date();
    const firstDay = formatDateInput(new Date(today.getFullYear(), today.getMonth(), 1));
    const lastDay = formatDateInput(new Date(today.getFullYear(), today.getMonth() + 1, 0));
    return { firstDay, lastDay };
  };

  const [fromDate, setFromDate] = useState(getDefaultDates().firstDay);
  const [toDate, setToDate] = useState(getDefaultDates().lastDay);
  const [reportSearch, setReportSearch] = useState('');
  const [reportSection, setReportSection] = useState('all');
  const [reportProduct, setReportProduct] = useState('all');
  const [reportStatus, setReportStatus] = useState('all');

  const toLocalDayTimeMs = (value, endOfDay) => {
    if (!value) return null;
    const date = new Date(`${String(value).trim()}T00:00:00`);
    if (Number.isNaN(date.getTime())) return null;
    if (endOfDay) date.setHours(23, 59, 59, 999);
    else date.setHours(0, 0, 0, 0);
    return date.getTime();
  };

  // --- جلب البيانات ---
  const tenantId = currentTenantId || user?.tenant_id;
  const companyId = currentCompanyId || user?.company_id;
  const branchId = currentBranchId || user?.branch_id;
  const reportTicketsUrl =
    tenantId && companyId && branchId
      ? `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/tenant/${tenantId}/${companyId}/${branchId}`
      : null;
  const { data: reportTicketsResult, loading: reportTicketsLoading } = useFetch(
    reportTicketsUrl,
    Boolean(reportTicketsUrl)
  );
  const { data: supportSettingsResult } = useFetch(
    `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/settings`,
    true
  );
  const { data: statusesResult } = useFetch(
    `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/statuses`,
    true
  );

  const supportSettingsBase = normalizeSupportSettings(supportSettingsResult);
  const supportSettings = useMemo(() => ({
    ...supportSettingsBase,
    statuses: normalizeSupportSettings({ data: { statuses: statusesResult?.data || [] } }).statuses
  }), [statusesResult?.data, supportSettingsBase]);

  const reportRows = useMemo(() => Array.isArray(reportTicketsResult?.data) ? reportTicketsResult.data : [], [reportTicketsResult?.data]);

  // --- خيارات الفلاتر ---
  const reportSectionOptions = useMemo(() => [
    { label: isAr ? 'كل الأقسام' : 'All Sections', value: 'all', lang: currentLangId },
    ...(supportSettings?.sections || []).filter((item) => item?.isActive !== false).map((item) => ({
      label: currentLangId === 'ar' ? item.labelAr : item.labelEn,
      value: item.key,
      lang: currentLangId,
    })),
  ], [currentLangId, isAr, supportSettings?.sections]);

  const reportProductOptions = useMemo(() => [
    { label: isAr ? 'كل الخدمات / المنتجات' : 'All Products', value: 'all', lang: currentLangId },
    ...(supportSettings?.products || []).filter((item) => item?.isActive !== false).map((item) => ({
      label: currentLangId === 'ar' ? item.labelAr : item.labelEn,
      value: item.key,
      lang: currentLangId,
    })),
  ], [currentLangId, isAr, supportSettings?.products]);

  const selectedReportSection = useMemo(() => reportSectionOptions.find((item) => item.value === reportSection) || reportSectionOptions[0], [reportSection, reportSectionOptions]);
  const selectedReportProduct = useMemo(() => reportProductOptions.find((item) => item.value === reportProduct) || reportProductOptions[0], [reportProduct, reportProductOptions]);

  // --- منطق الفلترة ---
  const reportCountRows = useMemo(() => reportRows.filter((item) => {
    const matchesSection = reportSection === 'all' || item.sectionId === reportSection;
    const matchesProduct = reportProduct === 'all' || item.ProductId === reportProduct;
    const searchLower = String(reportSearch || '').toLowerCase().trim();
    const matchesSearch = !searchLower || String(item.ticketNumber || '').toLowerCase().includes(searchLower) || String(item.subject || '').toLowerCase().includes(searchLower);
    
    const itemDate = new Date(item.createdAt).getTime();
    const fromTime = toLocalDayTimeMs(fromDate, false) ?? 0;
    const toTime = toLocalDayTimeMs(toDate, true) ?? Infinity;
    const matchesDate = itemDate >= fromTime && itemDate <= toTime;

    return matchesSection && matchesProduct && matchesSearch && matchesDate;
  }), [reportProduct, reportRows, reportSearch, reportSection, fromDate, toDate]);

  const reportStatusCounts = useMemo(() => {
    const counts = new Map();
    reportCountRows.forEach((item) => {
      const currentStatusValue = item?.statusId || item?.status;
      const statusId = getStatusId(supportSettings?.statuses, currentStatusValue);
      if (!statusId) return;
      counts.set(statusId, (counts.get(statusId) || 0) + 1);
    });
    return counts;
  }, [reportCountRows, supportSettings?.statuses]);

  const reportFiltered = useMemo(() => reportCountRows.filter((item) => {
    const currentStatusValue = item?.statusId || item?.status;
    const matchesStatus = reportStatus === 'all' || getStatusId(supportSettings?.statuses, currentStatusValue) === reportStatus;
    return matchesStatus;
  }), [reportCountRows, reportStatus, supportSettings?.statuses]);

  const formatReportDate = (value) => {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const getReportTicketRow = (ticket) => {
    const section = getCatalogLabel(supportSettings?.sections, ticket?.sectionId, currentLangId);
    const product = getCatalogLabel(supportSettings?.products, ticket?.ProductId, currentLangId);
    const statusValue = ticket?.statusId || ticket?.status;
    const status = getStatusLabel(supportSettings?.statuses, statusValue, currentLangId);
    return {
      ticketNumber: ticket?.ticketNumber || '',
      subject: ticket?.subject || '',
      section,
      product,
      createdAt: formatReportDate(ticket?.createdAt),
      updatedAt: formatReportDate(ticket?.modifiedAt || ticket?.updatedAt),
      status
    };
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const escapeCell = (value) => String(value || '').replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');

  const exportReportExcel = () => {
    const rowsForExport = reportFiltered.map(getReportTicketRow);
    const title = isAr ? 'تقرير التذاكر' : 'Tickets Report';
    const tableRows = rowsForExport.map((r) => `<tr><td>${escapeCell(r.ticketNumber)}</td><td>${escapeCell(r.subject)}</td><td>${escapeCell(r.section)}</td><td>${escapeCell(r.product)}</td><td>${escapeCell(r.createdAt)}</td><td>${escapeCell(r.updatedAt)}</td><td>${escapeCell(r.status)}</td></tr>`).join('');
    const html = `<html dir="${isAr ? 'rtl' : 'ltr'}"><head><meta charset="utf-8"/></head><body><h3>${title}</h3><table border="1"><thead><tr><th>${isAr ? 'رقم التذكرة' : 'Ticket #'}</th><th>${isAr ? 'عنوان التذكرة' : 'Subject'}</th><th>${isAr ? 'القسم' : 'Section'}</th><th>${isAr ? 'المنتج / الخدمة' : 'Product'}</th><th>${isAr ? 'تاريخ الإنشاء' : 'Created At'}</th><th>${isAr ? 'آخر تحديث' : 'Updated At'}</th><th>${isAr ? 'الحالة' : 'Status'}</th></tr></thead><tbody>${tableRows}</tbody></table></body></html>`;
    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8' });
    downloadBlob(blob, `tickets-report-${new Date().toISOString().slice(0, 10)}.xls`);
  };

  const printReport = () => {
    const rowsForExport = reportFiltered.map(getReportTicketRow);
    const title = isAr ? 'تقرير التذاكر' : 'Tickets Report';
    const tableRows = rowsForExport.map((r) => `<tr><td>${escapeCell(r.ticketNumber)}</td><td>${escapeCell(r.subject)}</td><td>${escapeCell(r.section)}</td><td>${escapeCell(r.product)}</td><td>${escapeCell(r.createdAt)}</td><td>${escapeCell(r.updatedAt)}</td><td>${escapeCell(r.status)}</td></tr>`).join('');
    const html = `<html dir="${isAr ? 'rtl' : 'ltr'}"><head><meta charset="utf-8"/><title>${title}</title><style>body { font-family: Arial, sans-serif; padding: 20px; } table { width: 100%; border-collapse: collapse; } th, td { border: 1px solid #e2e8f0; padding: 8px; font-size: 12px; } th { background: #f8fafc; text-align: ${isAr ? 'right' : 'left'}; }</style></head><body><h2>${title}</h2><table><thead><tr><th>${isAr ? 'رقم التذكرة' : 'Ticket #'}</th><th>${isAr ? 'عنوان التذكرة' : 'Subject'}</th><th>${isAr ? 'القسم' : 'Section'}</th><th>${isAr ? 'المنتج / الخدمة' : 'Product'}</th><th>${isAr ? 'تاريخ الإنشاء' : 'Created At'}</th><th>${isAr ? 'آخر تحديث' : 'Updated At'}</th><th>${isAr ? 'الحالة' : 'Status'}</th></tr></thead><tbody>${tableRows}</tbody></table></body></html>`;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.onload = () => { win.focus(); win.print(); };
  };

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      {/* --- رأس الصفحة --- */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center pb-6 mb-6 border-b border-slate-200">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <span className="bg-indigo-100 text-indigo-600 p-2 rounded-lg">📊</span>
            {isAr ? 'تقارير التذاكر' : 'Tickets Reports'}
          </h1>
          <p className="text-slate-500 text-sm mt-1">{isAr ? 'تحليل ومتابعة أداء التذاكر' : 'Analyze and track ticket performance'}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {reportTicketsLoading ? (
          <div className="flex items-center justify-center p-16">
            <MwSpinnerButton withLabel={true} isFullCenter />
          </div>
        ) : (
          <div className="p-6">
            
            {/* --- كتلة الفلاتر المدمجة (سطر واحد متراص) --- */}
            <div className="flex flex-wrap items-end gap-3 mb-6 pb-6 border-b border-slate-100">
              {/* 1. فلاتر التاريخ (في الأول) */}
              <div className="min-w-[140px]">
                <label className="text-xs font-semibold text-slate-600 mb-1 block">{isAr ? 'من تاريخ' : 'From'}</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-slate-50 text-slate-800 text-sm cursor-pointer text-center"
                />
              </div>
              <div className="min-w-[140px]">
                <label className="text-xs font-semibold text-slate-600 mb-1 block">{isAr ? 'إلى تاريخ' : 'To'}</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-slate-50 text-slate-800 text-sm cursor-pointer text-center"
                />
              </div>

              {/* 2. فلاتر الاختيار */}
              <div className="min-w-[150px] flex-1">
                <MwSelector
                  label={isAr ? 'المنتج' : 'Product'}
                  dataType="labelValue"
                  _data={reportProductOptions}
                  selectedItem={selectedReportProduct}
                  setSelectedItem={(item) => setReportProduct(item?.value || 'all')}
                  withAddNew={false}
                />
              </div>
              <div className="min-w-[150px] flex-1">
                <MwSelector
                  label={isAr ? 'القسم' : 'Section'}
                  dataType="labelValue"
                  _data={reportSectionOptions}
                  selectedItem={selectedReportSection}
                  setSelectedItem={(item) => setReportSection(item?.value || 'all')}
                  withAddNew={false}
                />
              </div>
              <div className="min-w-[180px] flex-[1.5]">
                <label className="text-xs font-semibold text-slate-600 mb-1 block">{isAr ? 'بحث سريع' : 'Quick Search'}</label>
                <input
                  value={reportSearch}
                  onChange={(e) => setReportSearch(e.target.value)}
                  placeholder={isAr ? 'ابحث برقم أو عنوان...' : 'Search...'}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-slate-50 text-slate-800 text-sm"
                />
              </div>

              {/* 4. فلاتر الألوان (الحالات) */}
              <div className="flex flex-wrap gap-1.5 items-center pt-1">
                {[
                  { label: isAr ? 'الكل' : 'All', value: 'all', bg: '#f1f5f9', color: '#334155', dot: '#64748b' },
                  ...getLocalizedStatusOptions(supportSettings?.statuses, currentLangId).map((opt) => ({
                    ...opt,
                    ...getStatusAppearance(supportSettings?.statuses, opt.value)
                  }))
                ].map((opt) => {
                  const isActive = (reportStatus || 'all') === opt.value;
                  const count = opt.value === 'all' ? (reportCountRows?.length || 0) : (reportStatusCounts.get(opt.value) || 0);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setReportStatus(opt.value || 'all')}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition-all ${
                        isActive ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                      }`}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ background: opt.dot || opt.color || '#64748b' }} />
                      <span className="text-xs font-bold text-slate-700 whitespace-nowrap">{opt.label}</span>
                      <span className="min-w-[22px] h-4 px-1 rounded-full bg-slate-100 text-slate-600 inline-flex items-center justify-center text-[10px] font-black">
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* --- أزرار الإجراءات في الأسفل (Excel و طباعة فقط) --- */}
            <div className="flex justify-end items-center gap-3 mb-4">
              <MwButton type="saveBtn" onClick={exportReportExcel} disabled={reportFiltered.length === 0} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
                <MdFileDownload size={18} /> {isAr ? 'تحميل Excel' : 'Download Excel'}
              </MwButton>
              <MwButton type="saveBtn" onClick={printReport} disabled={reportFiltered.length === 0} className="flex items-center gap-2 bg-slate-700 hover:bg-slate-800 text-white">
                <MdPrint size={18} /> {isAr ? 'طباعة' : 'Print'}
              </MwButton>
            </div>

            {/* --- جدول البيانات --- */}
            <div className="overflow-x-auto rounded-lg border border-slate-200 shadow-sm">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
                <span className="text-sm font-bold text-slate-700">
                  {isAr ? `نتائج التقرير: ${reportFiltered.length} تذكرة` : `Report Results: ${reportFiltered.length} Tickets`}
                </span>
              </div>
              
              {reportFiltered.length === 0 ? (
                <div className="text-center py-16 text-slate-500 bg-white">
                  <div className="text-4xl mb-3">📭</div>
                  <p className="font-medium">{isAr ? 'لا توجد بيانات مطابقة للفلاتر المحددة' : 'No data matches the selected filters'}</p>
                </div>
              ) : (
                <table className="w-full text-sm text-left rtl:text-right text-slate-600">
                  <thead className="text-xs text-slate-700 uppercase bg-slate-100 border-b border-slate-200 sticky top-0 z-10">
                    <tr>
                      {/* منع التفاف رقم التذكرة */}
                      <th className="px-4 py-3 font-bold whitespace-nowrap">{isAr ? 'رقم التذكرة' : 'Ticket #'}</th>
                      <th className="px-4 py-3 font-bold">{isAr ? 'العنوان' : 'Subject'}</th>
                      <th className="px-4 py-3 font-bold">{isAr ? 'القسم' : 'Section'}</th>
                      <th className="px-4 py-3 font-bold">{isAr ? 'المنتج' : 'Product'}</th>
                      <th className="px-4 py-3 font-bold">{isAr ? 'معينة لـ' : 'Assigned To'}</th>
                      <th className="px-4 py-3 font-bold">{isAr ? 'تاريخ الإنشاء' : 'Created At'}</th>
                      <th className="px-4 py-3 font-bold">{isAr ? 'الحالة' : 'Status'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {reportFiltered.slice(0, 100).map((ticket, idx) => {
                      const row = getReportTicketRow(ticket);
                      const statusStyle = getStatusAppearance(supportSettings?.statuses, ticket?.statusId || ticket?.status);
                      return (
                        <tr key={idx} className="hover:bg-slate-50 transition-colors">
                          {/* منع التفاف رقم التذكرة في الخانة أيضاً */}
                          <td className="px-4 py-3 font-mono font-medium text-slate-900 whitespace-nowrap">{row.ticketNumber}</td>
                          <td className="px-4 py-3 font-medium text-slate-800">{row.subject}</td>
                          <td className="px-4 py-3">{row.section}</td>
                          <td className="px-4 py-3">{row.product}</td>
                          <td className="px-4 py-3">{row.assignee}</td>
                          <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{row.createdAt}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: `${statusStyle?.bg}25`, color: statusStyle?.color }}>
                              {row.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {reportFiltered.length > 100 && (
                <div className="text-xs text-slate-500 py-3 px-4 bg-slate-50 border-t border-slate-200 text-center">
                  {isAr ? `يتم عرض أول 100 نتيجة فقط. استخدم Excel لعرض كافة البيانات.` : `Showing first 100 results. Use Excel export to view all.`}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
